/**
 * The acknowledgement fold: what the log says about each target, and whether an item is handled.
 *
 * The log is facts, one row per action, never updated; everything here is interpretation, decided when the log is read
 * so a rule can change without touching stored rows. Per target, rows fold as a stack: `acknowledged` pushes and
 * `unacknowledged` pops the newest push, so undo restores what stood before. A feed's rows fold the same way into its
 * watermark, which covers what arrived at or before its `through` and is never fanned out to items. Browser-safe and
 * free of every dependency but Luxon, so a server-side count can fold the same log the same way.
 */

import { DateTime, Duration } from "luxon";

/** How far back the log is read; anything that arrived earlier counts as acknowledged by age. */
export const ACKNOWLEDGEMENT_LOOKBACK_DAYS = 7;

export const ACKNOWLEDGEMENT_LOOKBACK_MS = Duration.fromObject({ days: ACKNOWLEDGEMENT_LOOKBACK_DAYS }).toMillis();

/**
 * One acknowledgement row, as much of it as the fold reads.
 */
export type AcknowledgementRecord = {
  id: string;
  action: "acknowledged" | "unacknowledged";
  /** The server's instant for the row, ISO: the order the fold reads rows in. */
  createdAt: string;
  /** A feed watermark's coverage, ISO; absent or null on every other row. */
  through?: string | null;
  /** Who wrote the row, for a receipt to name; the fold itself never reads it. */
  actorWorkspaceMembershipId?: string | null;
};

/**
 * One target's rows, folded.
 */
export type TargetAcknowledgement = {
  /** The acknowledgements no unacknowledge has popped, oldest first; the last is the one in force. */
  standing: readonly AcknowledgementRecord[];
  /** The newest unacknowledge's instant, popped or not, which frees the target from an older watermark. */
  lastUnacknowledgedAt: string | null;
};

/**
 * A feed's watermark in force: what it covers through, and when it was written.
 */
export type FeedWatermark = { through: string; createdAt: string };

export const NO_ACKNOWLEDGEMENTS: TargetAcknowledgement = Object.freeze({
  standing: Object.freeze([]) as readonly AcknowledgementRecord[],
  lastUnacknowledgedAt: null,
});

/** An ISO instant as epoch milliseconds, the clock every fold compares against. */
export const instant = (iso: string): number => Date.parse(iso);

/**
 * Orders rows as the log does: `(createdAt, id)` ascending, so rows of one write fold the same way everywhere.
 *
 * Every log shares the keyset, so a task's rows and its pins order by this too.
 */
export function byLogOrder<T extends { createdAt: string; id: string }>(a: T, b: T): number {
  return instant(a.createdAt) - instant(b.createdAt) || byteOrder(a.id, b.id);
}

/**
 * Compares two ids by code unit, as Postgres orders `text` under the C collation the log's keyset uses.
 *
 * Not `localeCompare`, whose locale rules can order two ids differently from the server's page.
 */
function byteOrder(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  return a < b ? -1 : 1;
}

/**
 * Folds one target's rows into its stack.
 *
 * Rows may come in any order and may repeat (a sync re-serves rows near its cursor); they are ordered and deduplicated
 * by id first. An unacknowledge on an empty stack pops nothing, but still records its instant.
 */
export function foldAcknowledgements(records: Iterable<AcknowledgementRecord>): TargetAcknowledgement {
  const unique = new Map<string, AcknowledgementRecord>();
  for (const record of records) {
    unique.set(record.id, record);
  }
  if (unique.size === 0) {
    return NO_ACKNOWLEDGEMENTS;
  }

  const stack: AcknowledgementRecord[] = [];
  let lastUnacknowledgedAt: string | null = null;
  for (const record of [...unique.values()].sort(byLogOrder)) {
    if (record.action === "acknowledged") {
      stack.push(record);
    } else {
      stack.pop();
      lastUnacknowledgedAt = record.createdAt;
    }
  }
  return { standing: stack, lastUnacknowledgedAt };
}

/**
 * Whether the target's own stack stands acknowledged.
 */
export function isStandingAcknowledged(target: TargetAcknowledgement): boolean {
  return target.standing.length > 0;
}

/**
 * The watermark a feed's folded rows leave in force, or null when none stands.
 *
 * A standing row written without `through` covers through its own instant, which is the most it could have seen.
 */
export function watermarkOf(feed: TargetAcknowledgement): FeedWatermark | null {
  const top = feed.standing.at(-1);
  if (top === undefined) {
    return null;
  }
  return { through: top.through ?? top.createdAt, createdAt: top.createdAt };
}

/**
 * What the rule reads about one item: when it arrived, its own fold, and its channel's watermark.
 */
export type AcknowledgementQuestion = {
  /** When the item reached the server, ISO: a message's `createdAt`, a TMS event's row `createdAt`. */
  arrivedAt: string;
  own: TargetAcknowledgement;
  watermark: FeedWatermark | null;
  /** The clock the lookback is measured from, as epoch milliseconds. */
  now: number;
};

/**
 * Whether an item counts as handled.
 *
 * It is when it arrived before the lookback, when its own stack stands acknowledged, or when its channel's watermark
 * covers its arrival and it was not unacknowledged on its own after that watermark was written.
 */
export function isAcknowledged({ arrivedAt, own, watermark, now }: AcknowledgementQuestion): boolean {
  const arrived = instant(arrivedAt);
  if (DateTime.fromMillis(arrived) < DateTime.fromMillis(now).minus({ milliseconds: ACKNOWLEDGEMENT_LOOKBACK_MS })) {
    return true;
  }
  if (isStandingAcknowledged(own)) {
    return true;
  }
  if (watermark === null || instant(watermark.through) < arrived) {
    return false;
  }
  return !(own.lastUnacknowledgedAt !== null && instant(own.lastUnacknowledgedAt) > instant(watermark.createdAt));
}
